import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../../pages/DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import ScrollIndicator from "../../../pages/Home/ScrollIndicator";

import { useUserReglements } from "./../../../dashboard/pages/user/Reglements/hooks/useUserReglements";

import ReglementsHero from "./components/ReglementsHero";
import ReglementsToolbar from "./components/ReglementsToolbar";
import ReglementCard from "./components/ReglementCard";
import ReglementsTable from "./../../../dashboard/pages/user/Reglements/components/ReglementsTable";
import ReglementSkeleton from "./components/ReglementSkeleton";
import ReglementsEmptyState from "./components/ReglementsEmptyState";
import PaymentPlanDrawer from "./components/PaymentPlanDrawer";
import PaymentSuccessBanner from "./components/PaymentSuccessBanner";
import { FaVoteYea } from "react-icons/fa";

export default function UsersReglementsDashboard() {
  useScrollToTop();

  const {
    user,
    userRole,

    reglements,
    filteredReglements,
    visibleReglements,
    seasons,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    maxPayments,
    paymentInterval,
    sessionId,

    payingId,
    notifyingId,
    isAlreadyNotified,

    selectedArticle,
    paymentPlanOpen,
    closePaymentPlan,
    handlePaymentPlanSaved,

    handlePayerClick,
    openPaymentPlan,
    handleNotifyAdmins,

    refresh,
    handleGenerateInvoice,
  } = useUserReglements();

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Mes Commandes & Règlements"
          subtitle="Gère tes règlements en cours, effectue tes paiements et consulte l'historique de tes transactions."
          icon={FaVoteYea}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <ReglementsHero stats={stats} loading={loading} onRefresh={refresh} />

        {sessionId && (
          <div className="mt-4">
            <PaymentSuccessBanner sessionId={sessionId} onVerified={refresh} />
          </div>
        )}

        <section className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
            <ReglementsToolbar
              ui={ui}
              seasons={seasons}
              updateUi={updateUi}
              debouncedSearch={debouncedSearch}
              setDebouncedSearch={setDebouncedSearch}
              onReset={resetFilters}
              loading={loading}
              onRefresh={refresh}
              totalCount={reglements.length}
              filteredCount={filteredReglements.length}
            />
          </div>

          <div className="p-3 sm:p-5 lg:p-6">
            {loading ? (
              <div className="space-y-4">
                <div className="hidden xl:block">
                  <ReglementSkeleton variant="table" />
                </div>

                <div className="grid grid-cols-1 gap-4 xl:hidden">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <ReglementSkeleton key={index} />
                  ))}
                </div>
              </div>
            ) : filteredReglements.length === 0 ? (
              <ReglementsEmptyState onReset={resetFilters} />
            ) : (
              <>
                <div className="hidden xl:block">
                  <ReglementsTable
                    reglements={visibleReglements}
                    payingId={payingId}
                    notifyingId={notifyingId}
                    isAlreadyNotified={isAlreadyNotified}
                    onPay={handlePayerClick}
                    onInstallments={openPaymentPlan}
                    onNotify={handleNotifyAdmins}
                    onInvoice={handleGenerateInvoice}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 xl:hidden">
                  {visibleReglements.map((reglement) => (
                    <ReglementCard
                      key={reglement.id}
                      reglement={reglement}
                      payingId={payingId}
                      notifyingId={notifyingId}
                      isAlreadyNotified={isAlreadyNotified}
                      onPay={handlePayerClick}
                      onInstallments={openPaymentPlan}
                      onNotify={handleNotifyAdmins}
                      onInvoice={handleGenerateInvoice}
                    />
                  ))}
                </div>

                {visibleReglements.length < filteredReglements.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-[#013E8E] px-5 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-indigo-800 hover:shadow-xl"
                    >
                      Charger plus
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <PaymentPlanDrawer
        isOpen={paymentPlanOpen}
        onClose={closePaymentPlan}
        totalAmount={selectedArticle?.prix_total}
        maxPayments={maxPayments}
        paymentInterval={paymentInterval}
        article={selectedArticle}
        user={user}
        onSaved={handlePaymentPlanSaved}
      />

      <ToastContainer />
    </section>
  );
}
